import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { Badge, Card, Group, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import type { DatosInvoiceDetalle } from "@/lib/types";

/** Una validación sobre un campo extraído · réplica del set del sistema legacy. */
interface ValidacionInvoice {
  label: string;
  valor: string;
  ok: boolean;
}

const noVacio = (v: string | null | undefined) => !!v && v.trim() !== "";

/**
 * Construye el mismo set de validaciones que el legacy corre sobre los datos
 * extraídos por OCR: presencia de cada campo obligatorio, importe > 0 y fecha
 * de expedición válida. Devuelve la etiqueta, el valor obtenido y si pasa.
 */
export function construirValidacionesInvoice(inv: DatosInvoiceDetalle): ValidacionInvoice[] {
  return [
    { label: "Nombre o razón social del emisor", valor: inv.emisorNombre ?? "", ok: noVacio(inv.emisorNombre) },
    { label: "Número de identificación fiscal del emisor", valor: inv.emisorTaxId ?? "", ok: noVacio(inv.emisorTaxId) },
    { label: "Dirección del emisor", valor: inv.emisorDireccion ?? "", ok: noVacio(inv.emisorDireccion) },
    { label: "Fecha de expedición", valor: inv.fechaExpedicion ? inv.fechaExpedicion.slice(0, 10) : "", ok: noVacio(inv.fechaExpedicion) },
    { label: "Nombre o razón social del receptor", valor: inv.receptorNombre ?? "", ok: noVacio(inv.receptorNombre) },
    { label: "RFC del receptor", valor: inv.receptorRfc ?? "", ok: noVacio(inv.receptorRfc) },
    { label: "Importe total", valor: `${inv.importeTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })} ${inv.moneda}`, ok: inv.importeTotal > 0 },
    { label: "Moneda", valor: inv.moneda ?? "", ok: noVacio(inv.moneda) },
    { label: "Número de factura", valor: inv.numeroFactura ?? "", ok: noVacio(inv.numeroFactura) },
  ];
}

/** ¿Todos los campos obligatorios se extrajeron correctamente? */
export function invoiceValido(inv: DatosInvoiceDetalle): boolean {
  return construirValidacionesInvoice(inv).every((v) => v.ok);
}

/**
 * Tabla "Resultado de validaciones" (réplica del sistema legacy): por cada campo
 * extraído del PDF muestra el valor obtenido y si la validación pasó. Si alguno
 * falla, no fue posible extraer toda la información necesaria del PDF.
 */
export function ResultadoValidacionesInvoice({ inv }: { inv: DatosInvoiceDetalle }) {
  const validaciones = construirValidacionesInvoice(inv);
  const fallidas = validaciones.filter((v) => !v.ok).length;
  const todoOk = fallidas === 0;

  return (
    <Card padding="lg">
      <Group justify="space-between" align="center" className="mb-2">
        <Text fw={600}>Resultado de validaciones</Text>
        <Badge color={todoOk ? "exito" : "peligro"} variant="light">
          {todoOk ? "Todas correctas" : `${fallidas} con problema`}
        </Badge>
      </Group>
      {!todoOk && (
        <Text size="sm" c="peligro" className="mb-2">
          No fue posible extraer toda la información necesaria del PDF. Verifica el archivo o vuelve a subirlo.
        </Text>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <Table verticalSpacing="xs" horizontalSpacing="sm">
          <Thead>
            <Tr>
              <Th>Validación</Th>
              <Th>Valor obtenido</Th>
              <Th className="text-center">Resultado</Th>
            </Tr>
          </Thead>
          <Tbody>
            {validaciones.map((v) => (
              <Tr key={v.label}>
                <Td>
                  <Text size="sm" fw={500}>
                    {v.label}
                  </Text>
                </Td>
                <Td>
                  <Text size="sm" c={v.valor ? undefined : "dimmed"}>
                    {v.valor || "—"}
                  </Text>
                </Td>
                <Td className="text-center">
                  {v.ok ? (
                    <IconCircleCheck size={20} className="inline text-exito-600" aria-label="Correcto" />
                  ) : (
                    <IconAlertTriangle size={20} className="inline text-peligro-600" aria-label="Con problema" />
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </Card>
  );
}
